import React, { useContext, useMemo } from "react";
import {
  Divider,
  Modal,
  Grid,
  Text,
  IconButton,
  Icons,
} from "@pushpress/shared-components";
import PriceBreakdownLine from "./PriceBreakdownLine";
import { AppContext } from "../../../../context/app";
import { formatToUSD, toFixedConditional } from "../../../../utils/misc";
import { DiscountControllerOptionFragment } from "../../../../components/controllers/DiscountSelect/DiscountSelect-generated.hooks";
import { getDiscountDescription } from "../../../../components/controllers/DiscountSelect/DiscountSelect.utils";
import { Plan, PlanPrice, SubscriptionMigratePreview } from "../../../../apis/subscriptionApi";

type PriceBreakdownToSwitchPlanProps = {
  visible: boolean;
  subscriptionMigratePreview?: SubscriptionMigratePreview;
  planOption?: Plan;
  planPrice?: PlanPrice;
  discountOption?: DiscountControllerOptionFragment;
  onClose: () => void;
};

function PriceBreakdownToSwitchPlan({
  visible,
  subscriptionMigratePreview,
  discountOption,
  planOption,
  planPrice,
  onClose,
}: PriceBreakdownToSwitchPlanProps) {
  const {
    client: { currencyIso },
  } = useContext(AppContext);

  const setupFee = useMemo(() => {
    return subscriptionMigratePreview.fees.find(
      (fee) => fee.code === "setupFee",
    );
  }, [subscriptionMigratePreview.fees]);

  const transactionFee = useMemo(() => {
    return subscriptionMigratePreview.fees.find(
      (fee) => fee.code === "transactionFee",
    );
  }, [subscriptionMigratePreview.fees]);

  const tax = subscriptionMigratePreview.tax;

  const amount = planPrice ? planPrice?.amount : Number(planOption?.amount);
  const shouldShowSubtotal = tax || transactionFee || discountOption;

  return (
    <Modal visible={visible}>
      <Modal.Card>
        <div className="p-3">
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Text variant="heading-4">Price breakdown</Text>
            </Grid>
            <Grid item>
              <IconButton icon={Icons.X} onClick={onClose} />
            </Grid>
          </Grid>
        </div>

        <Divider />
        <div className="pl-3 pr-3 pt-3">
          {subscriptionMigratePreview.proration && (
              <PriceBreakdownLine
                label="Prorated cost (new plan)"
                value={formatToUSD(
                  subscriptionMigratePreview.proration?.newPlanProratedCost,
                  currencyIso,
                )}
                description={`The partial cost of the new plan before the first recurring bill (~${formatToUSD(
                  subscriptionMigratePreview.proration?.dailyPrice,
                )} * ${
                  subscriptionMigratePreview.proration?.remainingDays
                } days).`}
              />
            )}
          {!subscriptionMigratePreview.proration && (
            <PriceBreakdownLine
              label="New plan base cost"
              value={formatToUSD(amount, currencyIso)}
            />
          )}
          {discountOption && subscriptionMigratePreview.discount && (
            <PriceBreakdownLine
              label={`${discountOption?.name} (${getDiscountDescription(
                discountOption,
                currencyIso,
              )})`}
              value={formatToUSD(
                subscriptionMigratePreview.discount?.amount,
                currencyIso,
              )}
            />
          )}
          {!!(subscriptionMigratePreview?.unusedBalance > 0) && (
            <PriceBreakdownLine
              label="Unused balance (current plan)"
              value={formatToUSD(
                subscriptionMigratePreview?.unusedBalance,
                currencyIso,
              )}
              description="Base plan cost, discounts, tax"
            />
          )}
          {setupFee && (
            <PriceBreakdownLine
              label="Setup fee"
              value={formatToUSD(setupFee.fee, currencyIso)}
            />
          )}
          {shouldShowSubtotal && (
            <>
              <Divider className="mb-3" />
              <PriceBreakdownLine
                label="Subtotal"
                value={formatToUSD(
                  subscriptionMigratePreview.subtotal,
                  currencyIso,
                )}
                weight="600"
              />
              <Divider className="mb-3" />
              {transactionFee && (
                <PriceBreakdownLine
                  label={`${transactionFee.description} (
                  ${toFixedConditional(transactionFee.rate * 100)}%)`}
                  value={formatToUSD(transactionFee.fee, currencyIso)}
                />
              )}
              {tax && (
                <PriceBreakdownLine
                  label={`Tax (${toFixedConditional(
                    subscriptionMigratePreview.tax?.rate * 100,
                  )}%)`}
                  value={formatToUSD(
                    subscriptionMigratePreview.tax?.amount,
                    currencyIso,
                  )}
                />
              )}
            </>
          )}
          <Divider className="mb-3" />
          <PriceBreakdownLine
            label="Total"
            value={formatToUSD(subscriptionMigratePreview.total, currencyIso)}
            weight="600"
            variant="heading-5"
          />
        </div>
      </Modal.Card>
    </Modal>
  );
}

export default PriceBreakdownToSwitchPlan;
